// a utility hook that provides a stateful access to the current viewer

import {useQuery} from '@github-ui/react-query'
import {IS_SERVER} from '@github-ui/ssr-utils'
import {type User, useUser} from '@github-ui/use-user'

import type {AssigneePickerAssignee} from '../AssigneePicker'
import {fetchSafeViewer, safeViewerQueryKey} from './fetch-safe-viewer'

// Most surfaces serialize `current_user.id` as a GraphQL node ID, but some (e.g. the
// dashboard surface) serialize the numeric database id, which the picker would then send
// straight into GraphQL. Treat those payloads as unusable so we fetch the viewer instead.
function isViewerWithNodeId(user: User | AssigneePickerAssignee | null): user is AssigneePickerAssignee {
  // `id` is typed as a string but Rails may serialize it as a number, so check both.
  return !!user && typeof user.id === 'string' && user.id.length > 0 && !/^\d+$/.test(user.id)
}

/**
 * @deprecated use '@github-ui/item-picker-assignee-tsq' instead
 *
 * Tries to get viewer from app payload and does a query over the network if needed.
 */
export function useViewer(): AssigneePickerAssignee | null {
  const {currentUser} = useUser()
  // Prefer the viewer from the `useUser` hook, which reads it from the app payload.
  // This isn't always available (e.g. in memex the app payload is not used), so fall
  // back to fetching it from the server. `enabled` skips the request when we already
  // have the viewer or when rendering on the server.
  const viewerFromPayload = isViewerWithNodeId(currentUser) ? currentUser : null

  const {data: fetchedViewer} = useQuery({
    queryKey: safeViewerQueryKey(),
    queryFn: ({signal}) => fetchSafeViewer(signal),
    enabled: !IS_SERVER && !viewerFromPayload,
    // The viewer is effectively static for the lifetime of the page, so keep it
    // non-stale to avoid refetching /issues/viewer every time the picker mounts.
    staleTime: Infinity,
  })

  return viewerFromPayload ?? (fetchedViewer as AssigneePickerAssignee | null) ?? null
}
